import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, Save, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { UserAvailability } from '../types';

interface UserAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (availability: Partial<UserAvailability>) => void;
  availability?: UserAvailability | null;
  userName: string;
}

const UserAvailabilityModal: React.FC<UserAvailabilityModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  availability, 
  userName 
}) => {
  const [formData, setFormData] = useState({
    timezone: 'Europe/Madrid',
    dailySchedules: {
      0: { isWorking: false, start: '09:00', end: '18:00' }, // Sunday
      1: { isWorking: true, start: '09:00', end: '18:00' },  // Monday
      2: { isWorking: true, start: '09:00', end: '18:00' },  // Tuesday
      3: { isWorking: true, start: '09:00', end: '18:00' },  // Wednesday
      4: { isWorking: true, start: '09:00', end: '18:00' },  // Thursday
      5: { isWorking: true, start: '09:00', end: '18:00' },  // Friday
      6: { isWorking: false, start: '09:00', end: '18:00' }  // Saturday
    },
    breaks: [
      {
        start: '12:00',
        end: '13:00',
        label: 'Almuerzo'
      }
    ],
    blockedDates: [] as string[],
    isActive: true,
    isVisibleInPublicAgenda: true
  });

  const [newBlockedDate, setNewBlockedDate] = useState('');

  const daysOfWeek = [
    { value: 0, label: 'Domingo', short: 'Dom' },
    { value: 1, label: 'Lunes', short: 'Lun' },
    { value: 2, label: 'Martes', short: 'Mar' },
    { value: 3, label: 'Miércoles', short: 'Mié' },
    { value: 4, label: 'Jueves', short: 'Jue' },
    { value: 5, label: 'Viernes', short: 'Vie' },
    { value: 6, label: 'Sábado', short: 'Sáb' }
  ];

  const timezones = [
    { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
    { value: 'America/New_York', label: 'Nueva York (EST/EDT)' },
    { value: 'America/Los_Angeles', label: 'Los Ángeles (PST/PDT)' },
    { value: 'America/Mexico_City', label: 'Ciudad de México (CST/CDT)' },
    { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (ART)' },
    { value: 'America/Bogota', label: 'Bogotá (COT)' },
    { value: 'America/Santiago', label: 'Santiago (CLT/CLST)' },
    { value: 'UTC', label: 'UTC (Tiempo Universal)' }
  ];

  useEffect(() => {
    if (availability) {
      // Convert old format to new format if needed
      let dailySchedules = availability.dailySchedules;
      
      // Migration from old workingDays format
      if (!dailySchedules && (availability as any).workingDays) {
        const oldWorkingDays = (availability as any).workingDays;
        const oldWorkingHours = (availability as any).workingHours || { start: '09:00', end: '18:00' };
        
        dailySchedules = {};
        for (let day = 0; day <= 6; day++) {
          dailySchedules[day] = {
            isWorking: oldWorkingDays.includes(day),
            start: oldWorkingHours.start,
            end: oldWorkingHours.end
          };
        }
      }

      const defaultSchedules = {
        0: { isWorking: false, start: '09:00', end: '18:00' },
        1: { isWorking: true, start: '09:00', end: '18:00' },
        2: { isWorking: true, start: '09:00', end: '18:00' },
        3: { isWorking: true, start: '09:00', end: '18:00' },
        4: { isWorking: true, start: '09:00', end: '18:00' },
        5: { isWorking: true, start: '09:00', end: '18:00' },
        6: { isWorking: false, start: '09:00', end: '18:00' }
      };
      setFormData({
        timezone: availability.timezone,
        dailySchedules: dailySchedules || defaultSchedules,
        breaks: availability.breaks,
        blockedDates: availability.blockedDates,
        isActive: availability.isActive,
        isVisibleInPublicAgenda: availability.isVisibleInPublicAgenda ?? true
      });
    } else {
      setFormData({
        timezone: 'Europe/Madrid',
        dailySchedules: {
          0: { isWorking: false, start: '09:00', end: '18:00' },
          1: { isWorking: true, start: '09:00', end: '18:00' },
          2: { isWorking: true, start: '09:00', end: '18:00' },
          3: { isWorking: true, start: '09:00', end: '18:00' },
          4: { isWorking: true, start: '09:00', end: '18:00' },
          5: { isWorking: true, start: '09:00', end: '18:00' },
          6: { isWorking: false, start: '09:00', end: '18:00' }
        },
        breaks: [
          {
            start: '12:00',
            end: '13:00',
            label: 'Almuerzo'
          }
        ],
        blockedDates: [],
        isActive: true,
        isVisibleInPublicAgenda: true
      });
    }
  }, [availability]);

  const handleDayScheduleChange = (dayValue: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      dailySchedules: {
        ...prev.dailySchedules,
        [dayValue]: {
          ...prev.dailySchedules[dayValue],
          [field]: value
        }
      }
    }));
  };

  const handleCopyToAllDays = (dayValue: number) => {
    const sourceSchedule = formData.dailySchedules[dayValue];
    const newSchedules = { ...formData.dailySchedules };
    
    Object.keys(newSchedules).forEach(dayKey => {
      const day = Number(dayKey) as keyof typeof newSchedules;
      if (newSchedules[day].isWorking) {
        newSchedules[day] = {
          ...newSchedules[day],
          start: sourceSchedule.start,
          end: sourceSchedule.end
        };
      }
    });

    setFormData(prev => ({
      ...prev,
      dailySchedules: newSchedules
    }));
  };

  const handleAddBreak = () => {
    setFormData(prev => ({
      ...prev,
      breaks: [
        ...prev.breaks,
        {
          start: '14:00',
          end: '15:00',
          label: 'Descanso'
        }
      ]
    }));
  };

  const handleRemoveBreak = (index: number) => {
    setFormData(prev => ({
      ...prev,
      breaks: prev.breaks.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateBreak = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      breaks: prev.breaks.map((brk, i) => 
        i === index ? { ...brk, [field]: value } : brk
      )
    }));
  };

  const handleAddBlockedDate = () => {
    if (newBlockedDate && !formData.blockedDates.includes(newBlockedDate)) {
      setFormData(prev => ({
        ...prev,
        blockedDates: [...prev.blockedDates, newBlockedDate].sort()
      }));
      setNewBlockedDate('');
    }
  };

  const handleRemoveBlockedDate = (date: string) => {
    setFormData(prev => ({
      ...prev,
      blockedDates: prev.blockedDates.filter(d => d !== date)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const getWorkingDaysCount = () => {
    return Object.values(formData.dailySchedules).filter(schedule => schedule.isWorking).length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Configurar Disponibilidad - {userName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* General Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Timezone */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>Zona Horaria</span>
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zona horaria *
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Visibility Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Eye className="w-5 h-5" />
                <span>Visibilidad</span>
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Disponibilidad activa</p>
                    <p className="text-xs text-gray-600">Permite agendar reuniones contigo</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.isActive ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Visible en agenda pública</p>
                    <p className="text-xs text-gray-600">Aparece en la lista de agentes disponibles</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isVisibleInPublicAgenda: !formData.isVisibleInPublicAgenda })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.isVisibleInPublicAgenda ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.isVisibleInPublicAgenda ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Schedules */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Horarios por Día ({getWorkingDaysCount()} días laborables)</span>
            </h3>
            
            <div className="space-y-4">
              {daysOfWeek.map(day => {
                const schedule = formData.dailySchedules[day.value];
                return (
                  <div key={day.value} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`day-${day.value}`}
                            checked={schedule.isWorking}
                            onChange={(e) => handleDayScheduleChange(day.value, 'isWorking', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`day-${day.value}`} className="ml-2 text-sm font-medium text-gray-900">
                            {day.label}
                          </label>
                        </div>
                        {schedule.isWorking && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Laborable
                          </span>
                        )}
                      </div>
                      
                      {schedule.isWorking && (
                        <button
                          type="button"
                          onClick={() => handleCopyToAllDays(day.value)}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Copiar a todos los días laborables
                        </button>
                      )}
                    </div>

                    {schedule.isWorking && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Hora de inicio
                          </label>
                          <input
                            type="time"
                            value={schedule.start}
                            onChange={(e) => handleDayScheduleChange(day.value, 'start', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Hora de fin
                          </label>
                          <input
                            type="time"
                            value={schedule.end}
                            onChange={(e) => handleDayScheduleChange(day.value, 'end', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Breaks */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Descansos</h3>
              <button
                type="button"
                onClick={handleAddBreak}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Añadir Descanso</span>
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.breaks.map((brk, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={brk.label}
                    onChange={(e) => handleUpdateBreak(index, 'label', e.target.value)}
                    placeholder="Nombre del descanso"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="time"
                    value={brk.start}
                    onChange={(e) => handleUpdateBreak(index, 'start', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="time"
                    value={brk.end}
                    onChange={(e) => handleUpdateBreak(index, 'end', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveBreak(index)}
                    className="text-red-600 hover:text-red-800 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {formData.breaks.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No hay descansos configurados. Los descansos se aplican a todos los días laborables.
                </p>
              )}
            </div>
          </div>

          {/* Blocked Dates */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Fechas Bloqueadas</h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="date"
                  value={newBlockedDate}
                  onChange={(e) => setNewBlockedDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleAddBlockedDate}
                  disabled={!newBlockedDate}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  Bloquear Fecha
                </button>
              </div>
              
              {formData.blockedDates.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-900 mb-2">Fechas bloqueadas:</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.blockedDates.map(date => (
                      <div key={date} className="flex items-center space-x-2 bg-white px-3 py-1 rounded border">
                        <span className="text-sm text-gray-900">
                          {new Date(date).toLocaleDateString('es-ES')}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveBlockedDate(date)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Vista previa de disponibilidad</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p><strong>Días laborables:</strong> {getWorkingDaysCount()} días</p>
              <p><strong>Zona horaria:</strong> {timezones.find(tz => tz.value === formData.timezone)?.label}</p>
              <p><strong>Estado:</strong> {formData.isActive ? 'Activo' : 'Inactivo'}</p>
              <p><strong>Visible en agenda pública:</strong> {formData.isVisibleInPublicAgenda ? 'Sí' : 'No'}</p>
              {formData.breaks.length > 0 && (
                <p><strong>Descansos:</strong> {formData.breaks.map(b => `${b.label} (${b.start}-${b.end})`).join(', ')}</p>
              )}
              {formData.blockedDates.length > 0 && (
                <p><strong>Fechas bloqueadas:</strong> {formData.blockedDates.length} fechas</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>Guardar Disponibilidad</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserAvailabilityModal;